import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Compass,
  GraduationCap,
  Loader2,
  Map,
  ShieldCheck,
  Sparkles,
  Stamp,
  Train,
  Users,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { whatsappUrl } from "../lib/whatsapp";
import Footer from "../components/Footer";
import LiveAmbience from "../components/LiveAmbience";
import AsFeaturedIn from "../components/AsFeaturedIn";
import Honeypot from "../components/Honeypot";

const HERO_IMG =
  "https://images.unsplash.com/photo-1474487548417-781cb71495f3?w=2000&q=80";

const HOW_IT_WORKS = [
  {
    n: "01",
    title: "Tell us your trip",
    body:
      "Year group, departure city, travel window, accessibility needs, dietary requirements. One form, ten minutes, no obligation.",
  },
  {
    n: "02",
    title: "Receive a vetted plan",
    body:
      "Within one business day: a 7-day itinerary, ABTA-style safety brief for every property, bulk rail pricing, and a single transparent invoice.",
  },
  {
    n: "03",
    title: "Travel with one phone line",
    body:
      "A named conductor on call 24/7 throughout the trip. Real-time train updates, supervised-minor protocols, photo log shared with parents nightly.",
  },
];

const TRUST_STRIP = [
  { icon: ShieldCheck, label: "ABTA-style safety brief" },
  { icon: Users, label: "1 staff : 8 pupils ratio" },
  { icon: Stamp, label: "Verified-for-minors stays" },
  { icon: Train, label: "Bulk rail discount 10-22%" },
];

const SAMPLE_ITINERARY = [
  { day: "Day 1 · Mon", city: "London → Paris", note: "Eurostar afternoon. Hostel check-in Marais, supervised group dinner." },
  { day: "Day 2 · Tue", city: "Paris", note: "Louvre AM, Eiffel PM, river walk back via Île de la Cité." },
  { day: "Day 3 · Wed", city: "Paris → Amsterdam", note: "Thalys morning. Anne Frank House (pre-booked), canal sunset cruise." },
  { day: "Day 4 · Thu", city: "Amsterdam", note: "Rijksmuseum AM, Van Gogh PM, free time in supervised pairs." },
  { day: "Day 5 · Fri", city: "Amsterdam → Berlin", note: "ICE direct. Brandenburg Gate twilight arrival, sausage supper." },
  { day: "Day 6 · Sat", city: "Berlin", note: "Reichstag dome (booked), Berlin Wall walk, Holocaust Memorial reflection." },
  { day: "Day 7 · Sun", city: "Berlin → home", note: "Morning at East Side Gallery. Afternoon flights / sleeper home." },
];

const TESTIMONIALS = [
  {
    quote:
      "Forty-two Year 11s, six teachers, three trains, zero stress. The night before, our InterQuest conductor sent every parent a personal welcome note. Genuinely the easiest school trip I've ever run.",
    name: "Naomi C.",
    role: "Head of Humanities, Highgate School",
  },
  {
    quote:
      "I was sceptical of a 'curated' service — assumed it meant a markup. The invoice came in 8% under what we'd budgeted with the old operator. The safety dossier alone was worth the difference.",
    name: "David M.",
    role: "Bursar, Cardiff High",
  },
  {
    quote:
      "When SNCF struck on day three, our conductor had rebooked all 38 of us on a coach + ferry combo before I'd even seen the news. That's the service.",
    name: "Aoife K.",
    role: "Trip Lead, Belvedere College Dublin",
  },
];

export default function Schools() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    phone: "",
    group_size: 30,
    age_range: "Year 11 (15-16)",
    travel_window: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [website, setWebsite] = useState(""); // honeypot
  const [testimonials, setTestimonials] = useState(TESTIMONIALS);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/testimonials")
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.testimonials;
        if (!cancelled && Array.isArray(list) && list.length > 0) {
          setTestimonials(list);
        }
      })
      .catch(() => {
        // Keep the hardcoded fallback so the page never renders empty.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (website) return; // honeypot tripped
    setErr("");
    setLoading(true);
    try {
      // Map school form fields → /api/group-inquiry contract
      const payload = {
        organization: form.organization,
        contact_name: form.name,
        contact_email: form.email,
        contact_phone: form.phone || null,
        group_size: Number(form.group_size) || 5,
        age_range: form.age_range || "school-age",
        travel_window: form.travel_window,
        special_requirements: form.notes || null,
        cities: [],
        website,
      };
      const { data } = await api.post("/group-inquiry", payload);
      setConfirm({
        ...data,
        message:
          "Our school-trip desk will reply within one business day with a written quote, safety brief, and recommended staff:pupil ratio.",
      });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="schools-page" className="pt-safe-navbar">
      {/* HERO */}
      <section className="relative min-h-[68vh] md:min-h-[72vh] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center animate-drift-slow"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/90 to-ink/50" />
        <LiveAmbience variant="hero" showTicker={true} embers={14} />

        <div className="relative max-w-[1400px] mx-auto px-5 md:px-6 pb-20 md:pb-24 pt-24 w-full">
          <div className="max-w-3xl animate-fade-up">
            <div className="iq-overline mb-4 flex items-center gap-2">
              <GraduationCap className="h-3 w-3" />
              For schools, sixth-forms &amp; universities
            </div>
            <h1 className="font-display text-cream text-[3.25rem] xs:text-6xl sm:text-7xl lg:text-[7rem] leading-[0.92] tracking-tight">
              SCHOOL TRIPS,
              <br />
              <span className="text-gold">FINALLY</span>
              <br />
              <span className="h-serif text-cream/90 text-3xl sm:text-5xl lg:text-7xl normal-case font-normal tracking-normal">
                done properly.
              </span>
            </h1>
            <p className="text-cream-dim text-base md:text-lg max-w-2xl mt-5 md:mt-6 font-serif italic leading-relaxed">
              Twenty-one safety-rated European cities. One named conductor on call.
              ABTA-style risk assessment in every quote. Bulk rail discounts up to
              22%. One invoice, no markups.
            </p>
            <div className="flex flex-wrap gap-3 mt-7 md:mt-8">
              <a
                href="#enquire"
                data-testid="schools-cta-quote"
                className="iq-btn-primary text-base"
              >
                Request a quote <ArrowRight className="h-5 w-5" />
              </a>
              <Link to="/routes" data-testid="schools-cta-routes" className="iq-btn-secondary">
                See the eight tours
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* AS FEATURED IN — press / accreditation strip */}
      <AsFeaturedIn />

      {/* TRUST STRIP */}
      <section className="bg-navy border-y border-panel-border">
        <div className="max-w-[1400px] mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {TRUST_STRIP.map(({ icon: Icon, label }, i) => (
            <div
              key={label}
              className="flex items-center gap-3 animate-fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
              data-testid={`schools-trust-${i}`}
            >
              <Icon className="h-6 w-6 text-gold shrink-0" strokeWidth={1.5} />
              <span className="font-display text-cream tracking-wider text-sm">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-[1400px] mx-auto px-6 py-24">
        <div className="mb-12">
          <div className="iq-overline mb-2">How it works</div>
          <h2 className="font-display text-cream text-5xl md:text-6xl tracking-wider leading-none">
            Three steps. One business day.
          </h2>
          <p className="font-serif italic text-cream-dim mt-3 max-w-xl text-lg">
            No call-centre. No price-per-clicks. A real human at the end of a real email.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {HOW_IT_WORKS.map((s, i) => (
            <div
              key={s.n}
              data-testid={`schools-step-${i}`}
              className="iq-card p-8 relative animate-fade-up hover:border-gold/40 transition-colors"
              style={{ animationDelay: `${i * 120}ms` }}
            >
              <div className="font-display text-gold text-7xl tracking-tight leading-none mb-4">
                {s.n}
              </div>
              <h3 className="font-display text-cream text-2xl tracking-wider mb-3">{s.title}</h3>
              <p className="text-cream-dim leading-relaxed text-sm">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SAMPLE 7-DAY ITINERARY */}
      <section className="bg-navy border-y border-panel-border">
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <div className="grid lg:grid-cols-[1fr_auto] items-end gap-6 mb-10">
            <div>
              <div className="iq-overline mb-2"><Map className="inline h-3 w-3 mr-1" /> Sample itinerary</div>
              <h2 className="font-display text-cream text-5xl md:text-6xl tracking-wider leading-none">
                Seven days · three capitals.
              </h2>
              <p className="font-serif italic text-cream-dim mt-3 max-w-xl text-lg">
                London → Paris → Amsterdam → Berlin. Built for Year 11–13 humanities trips.
              </p>
            </div>
            <Link to="/routes/grand-capital-tour" className="iq-btn-secondary text-sm py-2">
              Full tour spec <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="relative">
            {/* Track line */}
            <div className="absolute left-[14px] top-3 bottom-3 w-px bg-gradient-to-b from-gold/60 via-gold/30 to-transparent" />
            <div className="space-y-3">
              {SAMPLE_ITINERARY.map((d, i) => (
                <div
                  key={i}
                  className="relative pl-12 animate-fade-up"
                  style={{ animationDelay: `${i * 60}ms` }}
                  data-testid={`schools-itin-${i}`}
                >
                  <span className="absolute left-[8px] top-2.5 h-3 w-3 rounded-full bg-gold ring-4 ring-ink animate-pulse-gold" />
                  <div className="iq-card p-4 grid grid-cols-1 md:grid-cols-[140px_1fr_auto] gap-2 md:gap-4 md:items-center hover:border-gold/30 transition-colors">
                    <div className="font-display text-gold tracking-widest text-xs">{d.day}</div>
                    <div className="font-display text-cream tracking-wider text-base md:text-lg">{d.city}</div>
                    <div className="text-cream-dim text-sm font-serif italic leading-snug">{d.note}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-[1400px] mx-auto px-6 py-24">
        <div className="mb-10">
          <div className="iq-overline mb-2"><Sparkles className="inline h-3 w-3 mr-1" /> What trip leads say</div>
          <h2 className="font-display text-cream text-5xl md:text-6xl tracking-wider leading-none">
            Heads, bursars, &amp; trip leads.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {testimonials.map((t, i) => (
            <figure
              key={i}
              data-testid={`schools-testimonial-${i}`}
              className="iq-card p-7 animate-fade-up flex flex-col gap-4"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <div className="font-display text-gold text-5xl leading-none">“</div>
              <blockquote className="font-serif italic text-cream text-lg leading-relaxed flex-1">
                {t.quote}
              </blockquote>
              <figcaption className="border-t border-panel-border pt-3">
                <div className="font-display text-cream tracking-wider text-sm">{t.name}</div>
                <div className="text-cream-dim text-xs italic">{t.role}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* CONTACT FORM */}
      <section id="enquire" className="bg-navy border-t border-panel-border">
        <div className="max-w-[1100px] mx-auto px-6 py-24">
          <div className="grid lg:grid-cols-[1fr_420px] gap-10 items-start">
            <div>
              <div className="iq-overline mb-2"><Compass className="inline h-3 w-3 mr-1" /> Talk to a conductor</div>
              <h2 className="font-display text-cream text-5xl md:text-6xl tracking-wider leading-none mb-4">
                Get your quote.
              </h2>
              <p className="font-serif italic text-cream-dim text-lg leading-relaxed max-w-xl">
                Tell us the year group and the destination. We'll have a written
                plan, a safety brief, and a number-of-staff recommendation in
                your inbox within one business day. No payment until you say yes.
              </p>
              <ul className="mt-8 space-y-3 text-sm">
                {[
                  "Written safety dossier per property",
                  "Single transparent invoice — no surcharges",
                  "Named conductor on call 24/7 during trip",
                  "Free re-route if rail strikes affect your dates",
                ].map((p) => (
                  <li key={p} className="flex items-start gap-2 text-cream-dim">
                    <CheckCircle2 className="h-4 w-4 text-gold mt-0.5 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            {confirm ? (
              <div data-testid="schools-confirm" className="iq-card p-8 animate-fade-up">
                <CheckCircle2 className="h-10 w-10 text-gold mb-4" />
                <h3 className="font-display text-cream text-3xl tracking-wider mb-2">Enquiry received</h3>
                <p className="text-cream-dim text-sm font-display tracking-[0.3em] mb-4">
                  REF #{confirm.reference}
                </p>
                <p className="font-serif italic text-cream leading-relaxed mb-4">
                  "{confirm.message ||
                    'Our group desk will reply within one business day with a written quote and safety brief.'}"
                </p>
                <p className="text-xs text-cream-dim mb-5">
                  Confirmation sent to <span className="text-cream">{form.email}</span>.
                </p>
                <a
                  href={whatsappUrl({ schoolName: form.organization })}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="schools-confirm-whatsapp"
                  className="iq-btn-secondary w-full justify-center"
                >
                  Chat to the group desk on WhatsApp
                </a>
              </div>
            ) : (
              <form
                data-testid="schools-form"
                onSubmit={submit}
                className="iq-card p-7 space-y-4"
              >
                <Honeypot value={website} onChange={setWebsite} name="website" />
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="iq-label">Your name *</label>
                    <input
                      data-testid="schools-form-name"
                      required
                      className="iq-input"
                      value={form.name}
                      onChange={(e) => setF("name", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="iq-label">Email *</label>
                    <input
                      data-testid="schools-form-email"
                      required
                      type="email"
                      className="iq-input"
                      value={form.email}
                      onChange={(e) => setF("email", e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className="iq-label">School / organisation *</label>
                  <input
                    data-testid="schools-form-org"
                    required
                    className="iq-input"
                    value={form.organization}
                    onChange={(e) => setF("organization", e.target.value)}
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="iq-label">Group size *</label>
                    <input
                      data-testid="schools-form-size"
                      required
                      type="number"
                      min="5"
                      max="500"
                      className="iq-input"
                      value={form.group_size}
                      onChange={(e) => setF("group_size", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="iq-label">Year group / age *</label>
                    <input
                      data-testid="schools-form-age"
                      required
                      className="iq-input"
                      value={form.age_range}
                      onChange={(e) => setF("age_range", e.target.value)}
                      placeholder="Year 11 (15-16)"
                    />
                  </div>
                </div>
                <div>
                  <label className="iq-label">Phone</label>
                  <input
                    data-testid="schools-form-phone"
                    className="iq-input"
                    value={form.phone}
                    onChange={(e) => setF("phone", e.target.value)}
                    placeholder="+44 …"
                  />
                </div>
                <div>
                  <label className="iq-label">Travel window *</label>
                  <input
                    data-testid="schools-form-window"
                    required
                    className="iq-input"
                    value={form.travel_window}
                    onChange={(e) => setF("travel_window", e.target.value)}
                    placeholder="Half-term Feb 2027, 7 days"
                  />
                </div>
                <div>
                  <label className="iq-label">Notes (year group, subject, accessibility…)</label>
                  <textarea
                    data-testid="schools-form-notes"
                    className="iq-input min-h-[100px]"
                    value={form.notes}
                    onChange={(e) => setF("notes", e.target.value)}
                    placeholder="Year 11 GCSE History, 38 pupils + 6 staff, two vegetarians, one wheelchair user."
                  />
                </div>

                {err && (
                  <div data-testid="schools-form-error" className="border border-accent-red/40 bg-accent-red/10 text-cream px-4 py-3 rounded-sm text-sm">
                    {err}
                  </div>
                )}

                <button
                  data-testid="schools-form-submit"
                  type="submit"
                  disabled={loading}
                  className="iq-btn-primary w-full justify-center disabled:opacity-50"
                >
                  {loading ? (
                    <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</>
                  ) : (
                    <>Send enquiry <ArrowRight className="h-5 w-5" /></>
                  )}
                </button>
                <p className="text-[10px] text-cream-dim/60 italic text-center">
                  By submitting you agree to be contacted by our group desk. We never sell data.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
